import React, { useState } from "react"
import { Button, Group, Stack, Text, Code, Alert } from "@mantine/core"
import { Sonometer } from "sonometer/types/sonometer.model"
import {
    SonometerService,
    SonometerHighScoreUpdateReturn,
} from "services/sonometer.service"

const sonometerService = new SonometerService()

interface SonometerTestComponentProps {
    currentSonometer?: Sonometer
}

export function SonometerTestComponent({
    currentSonometer,
}: SonometerTestComponentProps) {
    const [isLoading, setIsLoading] = useState(false)
    const [response, setResponse] = useState<any>(null)
    const [error, setError] = useState<string | null>(null)

    const handleTestUpdateHighScore = async () => {
        if (!currentSonometer) {
            setError("No sonometer selected")
            return
        }

        setIsLoading(true)
        setError(null)
        setResponse(null)

        try {
            console.log("Sending request with sonometer:", currentSonometer)
            const result = await sonometerService.updateSonometerHighScore({
                sonometerToUpdate: currentSonometer,
            })
            console.log("Received response:", result)

            // Check if the response matches the expected type
            if (
                typeof result === "object" &&
                result !== null &&
                "message" in result &&
                "championshipId" in result &&
                "maxScore" in result &&
                "updatedSonometer" in result
            ) {
                // Response matches SonometerHighScoreUpdateReturn type
                setResponse(result as SonometerHighScoreUpdateReturn)
            } else {
                // Response is of unexpected type - dump as-is
                setResponse({
                    type: "unexpected",
                    rawResponse: result,
                })
            }
        } catch (err) {
            setError(
                `Error: ${err instanceof Error ? err.message : String(err)}`
            )
        } finally {
            setIsLoading(false)
        }
    }

    const formatResponse = (data: any): string => {
        try {
            return JSON.stringify(data, null, 2)
        } catch {
            return String(data)
        }
    }

    return (
        <Stack spacing="md">
            <Group position="apart">
                <Text size="lg" weight={600}>
                    Test Sonometer High Score Update
                </Text>
                <Button
                    className="bg-button-admin"
                    onClick={handleTestUpdateHighScore}
                    loading={isLoading}
                    disabled={!currentSonometer}
                    variant="filled"
                    color="green"
                    size="md"
                    style={{ fontWeight: "bold" }}
                >
                    🔥 Send High Score Update Request
                </Button>
            </Group>

            {!currentSonometer && (
                <Alert color="yellow" title="No Sonometer Selected">
                    Please select or create a sonometer first to test the high
                    score update.
                </Alert>
            )}

            {error && (
                <Alert color="red" title="Error">
                    {error}
                </Alert>
            )}

            {response && (
                <div>
                    <Text size="sm" weight={600} mb="xs">
                        Response:
                    </Text>
                    <Code
                        block
                        style={{
                            whiteSpace: "pre-wrap",
                            maxHeight: "400px",
                            overflow: "auto",
                        }}
                    >
                        {formatResponse(response)}
                    </Code>

                    {response.type === "unexpected" && (
                        <Alert
                            color="orange"
                            title="Unexpected Response Type"
                            mt="md"
                        >
                            The response does not match the expected
                            SonometerHighScoreUpdateReturn type. Raw response
                            has been dumped above.
                        </Alert>
                    )}
                </div>
            )}

            {currentSonometer && (
                <div>
                    <Text size="sm" weight={600} mb="xs">
                        Current Sonometer:
                    </Text>
                    <Code
                        block
                        style={{
                            whiteSpace: "pre-wrap",
                            maxHeight: "200px",
                            overflow: "auto",
                        }}
                    >
                        {formatResponse(currentSonometer)}
                    </Code>
                </div>
            )}
        </Stack>
    )
}
